/**
 * True only in the deployed Cloudflare Pages **production** environment —
 * `wrangler.toml`'s `[env.production]` is the one place that sets
 * `VITE_APP_ENV = "production"`.
 *
 * Deliberately does NOT match `VITE_APP_ENV=prod`. That value is set by `flake.nix`'s
 * `with-prod-backend` for a LOCAL dev server pointed at the production backend — a
 * developer's machine, which should behave like dev. Matching it would silently disable
 * dev-only surfaces during exactly the QA pass they most need to be visible in. The
 * deployed preview/staging environment and the Android internal build both set `"dev"`,
 * as does the SOPS-decrypted local `.env`.
 *
 * Fails toward production, on purpose: `vite.config.ts` falls back to Vite's `mode` when
 * the variable is unset, and `mode` is `"production"` for any `vite build`. So a deploy
 * that forgets the variable is treated as production rather than shipping dev behavior to
 * real users.
 *
 * Vitest does NOT get that build-time fallback (`vitest.config.ts` is a separate config
 * with no `define` for this key), but it DOES get the variable itself: Vite's default
 * `VITE_`-prefixed env exposure merges an exported `process.env.VITE_APP_ENV` over
 * whatever `.env` holds, for Vitest the same as for `vite build`/`dev`. So
 * `VITE_APP_ENV=production npm run test` genuinely runs with `isProduction === true`,
 * same as a deployed build; only the ambient SOPS `.env` (`dev`) or a wholly unset
 * variable land on the non-production branch. That is why the onboarding-redirect suites
 * (`CommunityDetail.onboardingRedirect.test.tsx`, `EventDetails.onboardingRedirect.test.tsx`,
 * `useOnboardingEntryRedirect.test.tsx`, `CommunityDetail.inviteUrl.test.tsx`) pin
 * `mutableFlagsForTests.onboardingEntryRedirect` explicitly rather than relying on the
 * ambient value.
 */
const isProduction = import.meta.env.VITE_APP_ENV === 'production';

const flags = {
  /**
   * Land users on `/events` instead of `/home`. Kill switch for the default
   * landing destination: set to false to restore `/home` landing everywhere.
   *
   * Read in one place, `routes.landing()`, which feeds both surfaces: the root
   * (`/`) redirect and the post-signup destination when the signup had no
   * specific origin to return to. Those origins (`/`, `/hi`, `/home`, and the
   * `/events` list) are all blocklisted from redirect storage by
   * `shouldStoreRedirectPath`, which is what routes them through this flag.
   * A signup that DID come from a specific page still returns there; that
   * branch never consults this flag. See `resolvePostSignupDestination`.
   */
  landOnEvents: true,
  // AIDEV-NOTE: useWebLocks must be true in production to prevent TOCTOU race conditions
  // that cause invalid_grant errors when multiple tabs refresh tokens simultaneously.
  /** Use Web Locks API for cross-tab token refresh synchronization */
  useWebLocks: true,
  /** Allow users to ask questions anonymously (dev/staging only) */
  anonymousAsking: false,
  /** Show "My network" visibility option in question creation (dev/staging only) */
  networkVisibility: false,
  /** Show the "Your meetball matches" matcher section to all users (admins always see it). */
  suggestedHelpers: true,
  /** Split Notifications into "Activity" + "You can help" tabs (matcher-activation helper feed). */
  helperFeed: false,
  /** Post-help invite share prompts (C3/C4). Admins always see them via has_extra_options. */
  invitePrompts: false,
  /** Live-event layer: badges, rings, pills, compact cards, boost chips. Kill switch. */
  liveEvents: true,
  /**
   * Helpable-at-live-event surfaces (For-you section, live-pill count chip, feed
   * chips). Governs the PUBLIC flip only: Meetball admins always see all three
   * surfaces (and their fetch) via has_extra_options (`useHelpableAtEventEnabled`),
   * flag-independent. Flipped ON for everyone in v4.16.0 (public launch).
   * Also selects the extra "who you can help" step of the first-Going welcome flow.
   */
  helpableAtEvent: true,
  /**
   * Magic-link (passwordless email) sign-in entry point. The UI routing control
   * for TECH-313 -- a BUILD-TIME flag, not a runtime kill switch; see the note
   * on the value below for what the real emergency stop is.
   *
   * Env-driven, unlike every other flag here: the value is read ONCE at module
   * init from `VITE_MAGIC_LINK_AUTH`, so turning it on per environment is a
   * wrangler.toml change, not a code change. Set to `"true"` in BOTH
   * `[env.preview.vars]` and `[env.production.vars]` as of the production
   * go-live, so the door is live everywhere. (This paragraph used to say
   * preview only, and stayed there after production was switched on; the
   * continuation block below pointed readers at it.)
   *
   * The comparison is deliberately exact: only the literal string `'true'`
   * enables the flag. Unset, `undefined`, `''`, `'1'` and `'TRUE'` all mean
   * false, so the flag fails closed in any environment that has not opted in.
   */
  magicLinkAuth: import.meta.env.VITE_MAGIC_LINK_AUTH === 'true',
  // BUILD-TIME, unlike every other flag in this object. Vite statically replaces
  // `import.meta.env.VITE_MAGIC_LINK_AUTH` with a literal during the build;
  // nothing reads an environment variable at runtime. Turning this off is:
  // edit wrangler.toml, commit, push, wait for a green CI and a Pages deploy --
  // minutes to tens of minutes -- and it does not reach a cached PWA client at
  // all until that client re-fetches its bundle. It is the UI ROUTING control,
  // not an emergency stop. The instrument that actually stops the feature fast
  // is server-side: unsetting MAGIC_LINK_HASH_SECRET makes the backend's
  // `is_allowed` fail closed and halts every send (see
  // magic_link_rate_limit.py's module docstring). Reach for that one first.

  /**
   * Magic-link CONTINUATION -- a link approved on one device signs in the
   * device that asked. Same build-time shape as `magicLinkAuth` above: read
   * ONCE at module init from `VITE_MAGIC_LINK_CONTINUATION`, exact-string
   * comparison, fails closed. Gated separately from `magicLinkAuth` because
   * continuation is a follow-on surface on top of magic-link sign-in, not a
   * prerequisite for it -- an environment can run magic-link auth without
   * continuation, but not the reverse (every continuation screen assumes
   * `magicLinkAuth` is already on).
   */
  magicLinkContinuation: import.meta.env.VITE_MAGIC_LINK_CONTINUATION === 'true',

  /**
   * The onboarding welcome-flow ENTRY REDIRECT (design §2) — the auto-bounce from
   * CommunityDetail/EventDetails into /communities/:slug/welcome. Kill switch.
   *
   * Scoped to the redirect ONLY, which is why it is not called `onboarding`. The legacy
   * GlobalOnboarding drawers (SpaceWelcome on the Spaces tab, EventGoingWelcome on first
   * RSVP) are a separate surface that design §12 deliberately decoupled from this flow, and
   * they keep firing with this off. The /welcome route also stays mounted, so the flow is
   * still reachable directly — that is how it is QA'd while this is off.
   *
   * NOT admin-bypassed (unlike helperFeed / invitePrompts). This gates a bounce, not a
   * surface: an `|| has_extra_options` arm would keep bouncing admins to /welcome with the
   * flag off, which is the opposite of what a kill switch is for.
   *
   * ENV-DERIVED, unlike every other flag here: ON everywhere except the deployed
   * production environment (see `isProduction` above for exactly what that means and what
   * it deliberately excludes). Onboarding stays exercisable in dev, staging, the Android
   * internal build and local shells — including `with-prod-backend` — while staying dark
   * for real users. Flipping it on in production is a one-word edit here (`!isProduction`
   * -> `true`); the kill-switch direction needs no edit at all, since production is
   * already off. `mutableFlagsForTests` still overrides it, so tests are unaffected by
   * the environment they run in as long as they set it explicitly.
   */
  onboardingEntryRedirect: !isProduction,
  /**
   * Polls open in the Post sheet over the feed (TECH-197 FE-4, `src/components/PostSheet`).
   * Off: `/questions/:slugOrId` renders today's post page straight away and no card opens the
   * sheet, exactly as before the sheet existed. On: a card for a poll (`kind === 'poll'`) opens
   * the sheet over its feed, and a cold start on a post URL waits behind the sheet's skeleton
   * until the post says what it is, so a poll never flashes the post page. Ordinary posts keep
   * the post page either way.
   *
   * Off until launch: with it on, an ordinary post opened cold waits behind the sheet's skeleton for its read before
   * the post page (the kind is known only after the read), and every route renders through PostSheetOutlet (TECH-197
   * FE-4b). Set `VITE_POST_SHEET` in `wrangler.toml` `[env.production.vars]` in the release before POLLS_ENABLED goes
   * on; native builds set it separately. Build-time and env-driven like `magicLinkAuth`: a local dev server exports
   * `VITE_POST_SHEET=true` to see polls in the sheet. Exact string only.
   */
  postSheet: import.meta.env.VITE_POST_SHEET === 'true',
};

export type FeatureFlags = Record<keyof typeof flags, boolean>;

/**
 * Runtime kill switches. Readonly to app code; tests flip via `mutableFlagsForTests`.
 *
 * Typed `boolean` (via `Record<keyof …, boolean>`), NOT their literal values: a literal type makes
 * every `if (featureFlags.x)` read as an "always truthy/falsy" lint error and defeats the toggle.
 * The `keyof typeof flags` key set keeps typo-safety on the flag names.
 */
export const featureFlags: Readonly<FeatureFlags> = flags;

/** TEST-ONLY seam — same object, mutable view. Import only from test files. */
export const mutableFlagsForTests: FeatureFlags = flags;
